/** Palette selection helpers for the art generator (named vs seeded random modes). */

export const RANDOM_MADRAS = 'Random Madras';
export const RANDOM_PALETTE = 'Random Palette';

/** Seeded generator modes that mint concrete hex colours at generation time. */
export function isSeededPaletteMode(name: unknown): boolean {
  return name === RANDOM_MADRAS || name === RANDOM_PALETTE;
}

/** Named palettes only — excludes Random Madras, Random Palette, and special modes. */
export function listNamedPalettes(paletteMap: Record<string, string[]>): string[] {
  return Object.keys(paletteMap).filter((name) => !isSeededPaletteMode(name));
}

/** Named palettes from the Palette dropdown groups (excludes seeded random modes). */
export function listNamedPalettesFromGroups(
  groups: ReadonlyArray<{ readonly palettes: readonly string[] }>
): string[] {
  const names: string[] = [];
  for (const group of groups) {
    for (const name of group.palettes) {
      if (!isSeededPaletteMode(name)) {
        names.push(name);
      }
    }
  }
  return names;
}

export function pickRandomNamedPalette(
  namedPalettes: readonly string[],
  random: () => number = Math.random
): string {
  if (!namedPalettes.length) {
    throw new Error('No named palettes available');
  }
  const index = Math.floor(random() * namedPalettes.length);
  return namedPalettes[Math.min(index, namedPalettes.length - 1)];
}

/**
 * One-shot initial palette for a fresh generator page load.
 * Never picks seeded random modes. Falls back when no named palettes exist.
 * Call only during state initialization — not during render or generation.
 */
export function pickInitialPagePalette(
  namedPalettes: readonly string[],
  fallback: string,
  random: () => number = Math.random
): string {
  if (!namedPalettes.length) {
    return fallback;
  }
  return pickRandomNamedPalette(namedPalettes, random);
}

/** Copy of a stored color list when it is usable as-is, else null. */
export function reusablePaletteColors(colors: readonly string[] | null | undefined): string[] | null {
  if (!Array.isArray(colors) || colors.length === 0) {
    return null;
  }
  if (!colors.every((c) => typeof c === 'string' && c.trim().length > 0)) {
    return null;
  }
  return [...colors];
}

export type ResolvedGenerationPalette = {
  /** UI / config palette selection (name, or stays a seeded random mode). */
  palette: string | string[];
  /** Concrete hex colors for this generation (seeded modes), else null. */
  paletteColors: string[] | null;
  /** When set, caller should update React palette state to this named palette. */
  uiPaletteUpdate: string | null;
};

/**
 * Resolve palette for one generation.
 * - Random toggle / random-all: pick a normal named palette (never seeded modes).
 * - Random Madras / Random Palette: derive colors from the generation seed.
 *   Existing colors are reused only for an explicit one-generation carry
 *   (preserveResolvedPalette), e.g. "Create with these traits".
 * - Otherwise: use the selected named palette as-is.
 */
export function resolveGenerationPalette(opts: {
  palette: string | string[];
  randomPaletteToggle: boolean;
  randomAll: boolean;
  namedPalettes: readonly string[];
  createRandomMadrasPalette: (seed?: string) => string[];
  createRandomHarmonyPalette?: (opts: {
    seed?: string;
    colorLimitMode?: string | null;
    colorLimit?: number | null;
  }) => string[];
  colorLimitMode?: string | null;
  colorLimit?: number | null;
  /** Resolved colors carried from an existing artwork for this generation only. */
  existingPaletteColors?: readonly string[] | null;
  /** Explicit opt-in: reuse existingPaletteColors instead of minting from the seed. */
  preserveResolvedPalette?: boolean;
  /** Generation seed; the deterministic source for seeded palette modes. */
  seed?: string;
}): ResolvedGenerationPalette {
  const {
    palette,
    randomPaletteToggle,
    randomAll,
    namedPalettes,
    createRandomMadrasPalette,
    createRandomHarmonyPalette,
    colorLimitMode,
    colorLimit,
    existingPaletteColors,
    preserveResolvedPalette,
    seed,
  } = opts;

  if (randomAll || randomPaletteToggle) {
    const picked = pickRandomNamedPalette(namedPalettes);
    return {
      palette: picked,
      paletteColors: null,
      uiPaletteUpdate: picked,
    };
  }

  if (palette === RANDOM_MADRAS) {
    const carried = preserveResolvedPalette ? reusablePaletteColors(existingPaletteColors) : null;
    return {
      palette: RANDOM_MADRAS,
      paletteColors: carried || createRandomMadrasPalette(seed),
      uiPaletteUpdate: null,
    };
  }

  if (palette === RANDOM_PALETTE) {
    const carried = preserveResolvedPalette ? reusablePaletteColors(existingPaletteColors) : null;
    const mint =
      createRandomHarmonyPalette ||
      (() => {
        throw new Error('createRandomHarmonyPalette is required for Random Palette');
      });
    return {
      palette: RANDOM_PALETTE,
      paletteColors:
        carried ||
        mint({
          seed,
          colorLimitMode,
          colorLimit,
        }),
      uiPaletteUpdate: null,
    };
  }

  return {
    palette,
    paletteColors: null,
    uiPaletteUpdate: null,
  };
}
